import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, IconButton, Typography, Chip } from "@mui/material";
import { Delete, Edit, RemoveRedEye, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetVolunteersQuery, useDeleteVolunteerMutation } from "./api/volunteersApi";
import { Volunteer } from "./types/volunteer";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditVolunteer from "./components/AddEditVolunteer";

const VolunteerManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
  const navigate = useNavigate();

  const { data: volunteers, isLoading, isError, isFetching } = useGetVolunteersQuery();
  const [deleteVolunteer] = useDeleteVolunteerMutation();

  // Debug: Log the volunteers data
  console.log('Volunteers data:', volunteers);
  console.log('Volunteers data type:', typeof volunteers);
  console.log('Is array:', Array.isArray(volunteers));
  console.log('Is loading:', isLoading);
  console.log('Is error:', isError);
  console.log('Is fetching:', isFetching);

  const columns: Array<MRT_ColumnDef<Volunteer>> = [
    {
      header: "Profile Image",
      accessorKey: "profile_image_url",
      size: 80,
      Cell({ row: { original } }) {
        return original.profile_image_url ? (
          <img
            src={original.profile_image_url}
            alt={original.name}
            width={60}
            height={60}
            style={{ borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            width={60}
            height={60}
            sx={{
              backgroundColor: 'grey.200',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
              {original.name.charAt(0).toUpperCase()}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.name || 'Unknown Volunteer'}
          </Typography>
        );
      },
    },
    {
      header: "Email",
      accessorKey: "email",
      size: 250,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2">
            {original.email || 'No email'}
          </Typography>
        );
      },
    },
    {
      header: "Phone",
      accessorKey: "phone",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2">
            {original.phone || 'No phone'}
          </Typography>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.status} 
            color={original.status === 'active' ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      header: "Availability",
      accessorKey: "availability",
      size: 120,
      Cell({ row: { original } }) {
        const isAvailable = original.availability === "1";
        return (
          <Chip 
            label={isAvailable ? 'Available' : 'Not Available'} 
            color={isAvailable ? 'primary' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      header: "Skills",
      accessorKey: "skills",
      size: 200,
      Cell({ row: { original } }) {
        const skills = original.skills || "";
        return (
          <Typography variant="body2" sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200
          }}>
            {skills.substring(0, 50)}{skills.length > 50 ? '...' : ''}
          </Typography>
        );
      },
    },
    {
      header: "Projects",
      accessorKey: "projects",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={`${original.projects?.length || 0} projects`}
            color="secondary" 
            size="small" 
          />
        );
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 120,
      Cell({ row: { original } }) {
        return new Date(original.created_at).toLocaleDateString();
      },
    },
    {
      header: "Actions",
      id: "actions",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/volunteers/${original.id}`);
              }}
              color="primary"
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVolunteer(original);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVolunteer(original);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedVolunteer) {
      try {
        await deleteVolunteer(selectedVolunteer.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedVolunteer(null);
      } catch (error) {
        console.error("Failed to delete volunteer:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedVolunteer(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Volunteer Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedVolunteer(null);
            setOpenDialog(true);
          }}
        >
          Add New Volunteer
        </Button>
      </Box>

      {isError && (
        <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2">
            Error loading volunteers. Please check the console for details.
          </Typography>
        </Box>
      )}

      <DataTable
        columns={columns}
        list={Array.isArray(volunteers) ? volunteers : (volunteers?.data || [])}
        count={Array.isArray(volunteers) ? volunteers.length : (volunteers?.data?.length || 0)}
        getRowId={(row) => row?.id?.toString() || Math.random().toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        pagination={pagination}
        onPaginationChange={setPagination}
        enableRowSelection={false}
        onRowClick={(row) => navigate(`/volunteers/${row.id}`)}
      />

      <AddEditVolunteer
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        volunteer={selectedVolunteer}
      />

      <ConfirmModal
        modalOpen={openConfirmModal}
        setModalOpen={setOpenConfirmModal}
        onOk={handleDelete}
        title="Delete Volunteer"
        message={`Are you sure you want to delete "${selectedVolunteer?.name || 'this volunteer'}"? This action cannot be undone.`}
        type="warning"
      />
    </Box>
  );
};

export default VolunteerManager;
